import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { apiList, apiCreate, apiUpdate, apiDelete } from '../api/apiClient';
import { getPDKCloudNodes, getPDKPanelDevices, getPDKSystemDevices, getPDKLiveDevices } from '../api/pdkApi';
// Seam API imports removed — Seam integration disabled
import { BrandPicker, LockBrand, LOCK_BRANDS } from '../components/locks/BrandPicker';
import { claimBridgeDevice } from '../api/bridgeApi';
import { getProperties } from '../api/properties';
import { getCustomers } from '../api/customers';
import { AuthContext } from '../context/AuthContext';
import { SystemDetailModal } from '../components/SystemDetailModal';
import { logAuditEvent } from '../api/auditLog';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { CountBand } from '../components/ui/CountBand';
import { SearchInput } from '../components/ui/SearchInput';
import { refreshAllCounters } from '../utils/refreshCounters';
import { formatDate } from '../utils/formatters';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProviderPill } from '../components/access/DeviceVisuals';
import { CBWSetupFlow } from '../components/access/CBWSetupFlow';
import { canAddDevices } from '../utils/roles';
import type { Role } from '../types';
import { AuthorizedResourceSetupModal } from '../components/integrations/AuthorizedResourceSetupModal';

interface ACSRecord {
  id: number;
  name: string;
  provider: string;
  property_id: number;
  system_id: string;
  pdk_system_id?: string;
  status: string;
  description?: string;
  created_at?: string;
  last_sync?: string;
  device_count?: number;
  cloud_node_count?: number;
  connected_account_id?: string;
  channel_count?: number;
  serial_number?: string;
  firmware_version?: string;
  _property?: any;
  // New system-level property-assignment fields (GET /acs-systems)
  assigned_property_name?: string | null;
  assigned_property_id?: string | null;
  opaque_device_count?: number;
  assigned_device_count?: number;
  customer_id?: string;
}

export const AccessControl: React.FC = () => {
  const { user } = useContext(AuthContext);
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();
  const [acsList, setAcsList] = useState<ACSRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAuthorizedSetup, setShowAuthorizedSetup] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<ACSRecord | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [allDevices, setAllDevices] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [pdkDeviceCounts, setPdkDeviceCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'table'|'grid'>(() => localStorage.getItem('view:access-control') === 'grid' ? 'grid' : 'table');

  // Assign-property mini-modal (triggered from "(Unassigned) — Assign" pill on the list row)
  const [assignTarget, setAssignTarget] = useState<ACSRecord | null>(null);
  const [assignProps, setAssignProps] = useState<any[]>([]);
  const [assignPropertyId, setAssignPropertyId] = useState<string>('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Modal state
  const [step, setStep] = useState(1);
  const [systemName, setSystemName] = useState('');
  const [systemId, setSystemId] = useState('');
  const [provider, setProvider] = useState<'pdk' | 'bridge' | 'brivo' | 'cbw' | 'unifi_access'>('pdk');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  // Per-cloud-node property assignment for the wizard's last step. Keys are
  // the PDK cloud-node id; values are the Intrlock property the PM picked.
  const [nodePropertyMap, setNodePropertyMap] = useState<Record<string, string>>({});
  // §28.518.5 — Step 7 groups: newly-created ACS system id, plus per-property
  // group-name overrides. Default: "Intrlock — {property name}"; user can rename.
  const [createdSystemId, setCreatedSystemId] = useState<string>('');
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [groupAuto, setGroupAuto] = useState<Record<string, boolean>>({});

  // Brivo state
  const [brivoUsername, setBrivoUsername] = useState('');
  const [brivoPassword, setBrivoPassword] = useState('');
  const [brivoSites, setBrivoSites] = useState<any[]>([]);
  const [selectedBrivoSite, setSelectedBrivoSite] = useState(0);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState('');

  // UniFi Access credentials are held only in component state until they are
  // POSTed to connector-api. connector-api KMS-encrypts the complete envelope;
  // the browser never persists the token in localStorage or an Intrlock row.
  const [unifiConsoleHost, setUnifiConsoleHost] = useState('');
  const [unifiApiToken, setUnifiApiToken] = useState('');
  const [unifiTlsFingerprint, setUnifiTlsFingerprint] = useState('');

  // Step 2 - Cloud nodes (PDK)
  const [cloudNodes, setCloudNodes] = useState<any[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());

  // Step 3 - Devices
  const [devices, setDevices] = useState<any[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Step 4 - Property
  const [properties, setProperties] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<LockBrand | null>(null);

  const fetchACS = async () => {
    try {
      setLoading(true);
      const [pdkData, bridgeData, brivoData, props, devs, custs] = await Promise.all([
        apiList('access_control_systems'),
        apiList('bridge_devices').catch(() => []),
        apiList('brivo_systems').catch(() => []),
        apiList('properties'),
        apiList('devices'),
        apiList('customers').catch(() => []),
      ]);
      setAllCustomers(Array.isArray(custs) ? custs : []);
      // Normalize bridge devices to ACS record shape
      const bridgeSystems = (Array.isArray(bridgeData) ? bridgeData : [])
        .filter((b: any) => b.claimed)
        .map((b: any) => ({ ...b, provider: 'bridge', system_id: b.serial_number }));
      const brivoSystems = (Array.isArray(brivoData) ? brivoData : [])
        .filter((b: any) => String(b.provider || b.provider_key || '').toLowerCase() === 'brivo' || !!b.brivo_account_id)
        .map((b: any) => ({ ...b, provider: 'brivo', system_id: b.brivo_account_id || b.system_uuid }));
      const allSystems = [
        ...(Array.isArray(pdkData) ? pdkData : []),
        ...bridgeSystems,
        ...brivoSystems,
      ]
        // Schlage rows back individual smart locks (not an ACS system) — hide them
        // here so users see Schlage only as devices on the Devices page.
        .filter((s: any) => String(s.provider || '').toLowerCase() !== 'schlage');
      // Each PDK cloud node is a separate ACS system in Intrlock.
      const seen = new Set<string>();
      const acsSystems = allSystems.filter((s: any) => {
        const provider = String(s.provider || '').toLowerCase();
        const key = `${provider}:${s.uuid || s.id || JSON.stringify(s)}`;
        if (seen.has(String(key))) return false;
        seen.add(String(key));
        return true;
      });
      setAcsList(acsSystems);
      setAllProperties(Array.isArray(props) ? props : []);
      setAllDevices(Array.isArray(devs) ? devs : []);
      // Fetch live device counts from PDK for each PDK system
      const counts: Record<string, number> = {};
      for (const sys of acsSystems.filter((s: any) => s.provider === 'pdk' && (s.system_id || s.pdk_system_id || s.acs_external_id))) {
        try {
          const pdkDevs = await getPDKSystemDevices(sys.system_id || sys.pdk_system_id || sys.acs_external_id);
          counts[sys.id] = pdkDevs.length;
        } catch { counts[sys.id] = -1; }
      }
      setPdkDeviceCounts(counts);
    } catch (e) {
      console.error('Failed to load ACS list:', e);
      setAcsList([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAcs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return acsList.filter((system: any) => {
      const provider = String(system.provider || system.type || '').toLowerCase();
      const status = String(system.status || 'unknown').toLowerCase();
      const haystack = [system.name, system.provider, system.system_id, system.pdk_system_id, system.assigned_property_name]
        .filter(Boolean).join(' ').toLowerCase();
      return (!q || haystack.includes(q)) && (providerFilter === 'all' || provider === providerFilter) && (statusFilter === 'all' || status === statusFilter);
    });
  }, [acsList, search, providerFilter, statusFilter]);

  useEffect(() => { fetchACS(); }, []);

  const getPropertyName = (propId: number) => {
    const p = allProperties.find((x: any) => x.id === propId);
    return p?.name || `Property #${propId}`;
  };

  // Find all properties that share the same pdk_system_id as this ACS record
  const getAssociatedProperties = (acs: ACSRecord) => {
    const sysId = acs.system_id || acs.pdk_system_id || '';
    if (!sysId) {
      // No system ID — just show the directly linked property
      const p = allProperties.find((x: any) => x.id === acs.property_id);
      return p ? [p] : [];
    }
    // Find all properties with the same pdk_system_id
    const matched = allProperties.filter((p: any) => p.pdk_system_id === sysId);
    if (matched.length > 0) return matched;
    // Fallback to direct property_id
    const p = allProperties.find((x: any) => x.id === acs.property_id);
    return p ? [p] : [];
  };

  const getDeviceCount = (acs: ACSRecord) => {
    // Use live PDK count if available
    if (acs.provider === 'pdk' && pdkDeviceCounts[acs.id] != null && pdkDeviceCounts[acs.id] >= 0) {
      return pdkDeviceCounts[acs.id];
    }
    return allDevices.filter((d: any) => d.property_id === acs.property_id).length;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const openDetail = (acs: ACSRecord) => {
    // Each system row → its own page where every option (sync, cloud-node
    // assignment, people mapping, devices, decisions) lives in one place.
    navigate(`/access-control/${(acs as any).id}`);
  };

  const resetModal = () => {
    setStep(1);
    setSystemName('');
    setSystemId('');
    setProvider('pdk');
    setSelectedCustomerId('');
    setConnectLoading(false);
    setConnectError('');
    setUnifiConsoleHost('');
    setUnifiApiToken('');
    setUnifiTlsFingerprint('');
    setCloudNodes([]);
    setSelectedNodes(new Set());
    setDevices([]);
    setSelectedDevices(new Set());
    setDevicesLoading(false);
    setSelectedPropertyId('');
    setNodePropertyMap({});
    setSaving(false);
    setSelectedBrand(null);
  };

  const openModal = async () => {
    resetModal();
    // Pre-load customers + properties so the Add ACS wizard's customer +
    // property dropdowns render without a per-step round-trip.
    try {
      const [custs, props] = await Promise.all([getCustomers(), getProperties()]);
      setCustomers(Array.isArray(custs) ? custs : []);
      setProperties(Array.isArray(props) ? props : []);
    } catch { /* non-fatal */ }
    setShowModal(true);
  };
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('setup');
    const supported = ['pdk', 'bridge', 'brivo', 'cbw', 'unifi_access'];
    if (!requested || !supported.includes(requested)) return;
    openModal().then(() => setProvider(requested as any));
    navigate('/access-control', { replace: true });
    // Marketplace setup links are consumed once so refresh cannot reopen a credential flow.
  }, [location.search]);
  const closeModal = () => { setShowModal(false); resetModal(); };

  // === PDK Flow ===
  const handlePDKConnect = async () => {
    if (!systemId.trim()) { setConnectError('Please enter a System ID'); return; }
    setConnectLoading(true);
    setConnectError('');
    try {
      const nodes = await getPDKCloudNodes(systemId.trim());
      setCloudNodes(nodes);
      setSelectedNodes(new Set());     // start empty — PM ticks the ones to assign
      setStep(4);                      // cloud-node selection step
    } catch (e: any) {
      setConnectError(e.message || 'Failed to connect to PDK system');
    } finally {
      setConnectLoading(false);
    }
  };

  // Seam flow removed — Seam integration disabled

  // === Brivo Flow ===
  const handleBrivoConnect = async () => {
    if (!brivoUsername || !brivoPassword) { setConnectError('Username and password required'); return; }
    if (!selectedCustomerId) { setConnectError('Please select a customer first'); return; }
    setConnectLoading(true);
    setConnectError('');
    try {
      const { setBrivoCredentials, getBrivoSites, getBrivoAccessPoints } = await import('../api/brivoApi');
      await setBrivoCredentials(brivoUsername, brivoPassword, BRIVO_API_KEY, '');
      const sites = await getBrivoSites();
      setBrivoSites(sites);
      const accessPoints = await getBrivoAccessPoints();
      setDevices(accessPoints.map((ap: any) => ({
        id: ap.id,
        name: ap.name || `Access Point ${ap.id}`,
        type: 'lock',
        connection_status: 'online',
        siteId: ap.siteId,
        siteName: ap.siteName || '',
        controlPanelId: ap.controlPanelId,
        activationEnabled: ap.activationEnabled,
        _selected: true,
      })));
      setSelectedDevices(new Set(accessPoints.map((ap: any) => String(ap.id))));
      setStep(5); // device selection
    } catch (e: any) {
      setConnectError(e.message || 'Failed to connect to Brivo');
    } finally {
      setConnectLoading(false);
    }
  };

  const BRIVO_API_KEY = 'qg3t3v5k4v2fk5zt678qau6u';

  // === Bridge Flow ===
  const handleBridgeNext = async () => {
    if (!selectedCustomerId) { setConnectError('Please select a customer first'); return; }
    try {
      const [props, custs] = await Promise.all([getProperties(), getCustomers()]);
      setProperties(Array.isArray(props) ? props : []);
      setCustomers(Array.isArray(custs) ? custs : []);
      setStep(6); // straight to property assignment
    } catch (e: any) {
      setConnectError(e.message || 'Failed to load properties');
    }
  };

  const handleUnifiNext = () => {
    const fingerprint = unifiTlsFingerprint.replace(/:/g, '').trim();
    if (!unifiConsoleHost.trim()) { setConnectError('Enter the UniFi console hostname or IP address.'); return; }
    if (!unifiApiToken.trim()) { setConnectError('Enter the API token created in UniFi Access.'); return; }
    if (!/^[A-Fa-f0-9]{64}$/.test(fingerprint)) {
      setConnectError('Enter the console certificate SHA-256 fingerprint (64 hexadecimal characters).');
      return;
    }
    setConnectError('');
    setStep(6);
  };

  // §28.518.6 — Step 4 → 5 now fetches devices per selected cloud node
  // from pdk-proxy so the PM can pick which devices to import. Skipping to
  // 6 (bulk import) was hiding the choice; owner directive: user picks
  // devices explicitly at Step 5 before property assignment at Step 6.
  const handleLoadDevices = async () => {
    if (selectedNodes.size === 0) { setStep(6); return; } // "Skip — assign later"
    setDevicesLoading(true);
    try {
      const allDevs: any[] = [];
      for (const cn of cloudNodes) {
        const pdkExtId = String((cn as any).id || (cn as any).acs_external_id || '');
        if (!selectedNodes.has(pdkExtId)) continue;
        const cnDevs = await getPDKPanelDevices(systemId.trim(), pdkExtId);
        for (const d of (cnDevs || [])) {
          allDevs.push({
            id: String(d.id),
            name: d.name || `Device ${d.id}`,
            type: d.type || 'door',
            _cloudNodeId: pdkExtId,
            _cloudNodeName: (cn as any).name || '',
            _selected: true,
          });
        }
      }
      setDevices(allDevs);
      setSelectedDevices(new Set(allDevs.map((d: any) => String(d.id))));
      setStep(5);
    } catch (e: any) {
      setConnectError(e.message || 'Failed to load devices from PDK');
    } finally { setDevicesLoading(false); }
  };

  // Step 5 → 6: From per-device selection (Brivo path), advance to
  // property assignment.
  const handleChooseProperty = async () => {
    setStep(6);
  };

  // Step 4: Save
  const handleSave = async () => {
    // For PDK in per-cloud-node mode, selectedPropertyId stays empty and
    // each CN has its own entry in nodePropertyMap. For Bridge / Brivo /
    // single-property PDK we still need a single selectedPropertyId.
    const hasPerNodeMap = Object.values(nodePropertyMap).some(Boolean);
    if (!selectedPropertyId && !hasPerNodeMap) return;
    setSaving(true);
    try {
      if (provider === 'pdk') {
        // One ACS system row per (customer × PDK system_id). The connect
        // endpoint upserts and pulls every cloud node, device, holder,
        // and group from PDK in one shot. The PM then assigns each cloud
        // node + each device to an Intrlock property from the detail
        // page — Intrlock never modifies pre-existing PDK records.
        const customerId = selectedCustomerId
          || String(properties.find((p: any) => String(p.uuid || p.id) === String(selectedPropertyId))?.customer_id || '');
        if (!customerId) throw new Error('Customer is required.');

        const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
        const tk = localStorage.getItem('intrlock_token') || '';
        const res = await fetch(`${apiBase}/acs-systems/connect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({
            customer_id:   customerId,
            pdk_system_id: systemId.trim(),
            name:          systemName || 'PDK System',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.error) {
          throw new Error(data?.error || `Connect failed (${res.status})`);
        }

        // §28.518.7 — Prune unchecked cloud nodes. User directive: non-selected
        // cloud nodes should NOT be visible after setup. We delete them (and any
        // devices that hung off them). Selected cloud nodes' devices stay in
        // the devices table even if the user doesn't override the type — they
        // can be assigned later on the detail page.
        if (selectedNodes.size > 0) {
          for (const cn of (data.cloud_nodes || [])) {
            const pdkExtId = (cn as any).acs_external_id || (cn as any).pdk_id || '';
            const isSelected = Array.from(selectedNodes).some(
              (k) => k === String(pdkExtId) || k === String((cn as any).id)
            );
            if (isSelected) continue;
            await fetch(`${apiBase}/acs-systems/cloud-nodes/${cn.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${tk}` },
            }).catch(() => {});
          }
        }

        // Per-cloud-node property assignment. The wizard's last step
        // collects either a single property (single-node systems) or one
        // property per ticked cloud node (multi-node systems). Map keys
        // are PDK external IDs; we translate to the freshly-imported
        // acs_cloud_nodes UUIDs returned by /connect.
        if (selectedNodes.size > 0) {
          for (const cn of (data.cloud_nodes || [])) {
            // The PDK external id lives in cn.acs_external_id (preferred)
            // or as a substring of the synced row.
            const pdkExtId =
              (cn as any).acs_external_id || (cn as any).pdk_id || '';
            const tickedKey = Array.from(selectedNodes).find(
              (k) => k === String(pdkExtId) || k === String((cn as any).id),
            );
            if (!tickedKey) continue;
            const propId = nodePropertyMap[tickedKey] || selectedPropertyId;
            if (!propId) continue;
            await fetch(`${apiBase}/acs-systems/cloud-nodes/${cn.id}/assign-property`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
              body: JSON.stringify({ property_id: propId }),
            }).catch(() => {});
          }
        }

        await logAuditEvent({
          property_id: selectedPropertyId || Object.values(nodePropertyMap)[0] || '',
          acs_system_id: data?.system?.id,
          event_type: 'system_connected',
          event_source: 'pdk',
          description: `Connected PDK system "${systemName || 'PDK System'}" — ${(data.cloud_nodes || []).length} sites`,
          metadata: { provider: 'pdk', system_id: systemId.trim(), cloud_node_count: (data.cloud_nodes || []).length },
        });

        // §28.518.5 — PDK path continues to Step 7 (Groups) instead of closing.
        // Prime default group names for each unique property that got assigned.
        const uniqueProps = Array.from(new Set(
          Object.values(nodePropertyMap).filter(Boolean).concat(selectedPropertyId ? [selectedPropertyId] : [])
        ));
        const nameSeed: Record<string, string> = {};
        const autoSeed: Record<string, boolean> = {};
        for (const pid of uniqueProps) {
          const p = properties.find((x: any) => String(x.id) === String(pid) || String(x.uuid) === String(pid));
          nameSeed[pid] = `Intrlock — ${p?.name || pid.slice(0, 8)}`;
          autoSeed[pid] = true;
        }
        setGroupNames(nameSeed);
        setGroupAuto(autoSeed);
        setCreatedSystemId(String(data?.system?.id || ''));
        setSaving(false);
        setStep(7);
        return;
      } else if (provider === 'brivo') {
        // Save Brivo devices
        for (const devId of Array.from(selectedDevices)) {
          const dev = devices.find((d: any) => String(d.id) === devId);
          if (!dev) continue;
          await apiCreate('devices', {
            device_name: dev.name || `Brivo Access Point ${dev.id}`,
            device_type: 'lock',
            property_id: selectedPropertyId,
            status: 'active',
            serial_number: `BRIVO-${dev.id}`,
            connection_status: 'online',
            acs_provider: 'brivo',
            acs_device_id: String(dev.id),
            model: dev.siteName || '',
            manufacturer: 'Brivo',
          });
        }
        const acsRecord = await apiCreate('brivo_systems', {
          name: systemName || 'Brivo System',
          brivo_account_id: '',
          brivo_username: brivoUsername,
          property_id: selectedPropertyId,
          status: 'connected',
          provider: 'brivo',
          access_point_ids_json: JSON.stringify(Array.from(selectedDevices)),
          system_uuid: crypto.randomUUID(),
        });
        await logAuditEvent({
          property_id: selectedPropertyId,
          acs_system_id: acsRecord?.id,
          event_type: 'system_connected',
          event_source: 'brivo',
          description: `Connected Brivo system "${systemName || 'Brivo System'}" with ${selectedDevices.size} access points`,
          metadata: { provider: 'brivo', device_count: selectedDevices.size },
        });
      } else if (provider === 'unifi_access') {
        // The shadow gateway maps ANY /integrations/{proxy+} directly to the
        // connector Lambda. The legacy /admin-connector prefix exposes only a
        // small PDK-specific route set and does not include verify/enable.
        const connectorBase = window.location.hostname.startsWith('shadow.')
          ? 'https://shadow.api.intrlock.io'
          : `${process.env.REACT_APP_API_URL || 'https://api.intrlock.io'}`;
        const tk = localStorage.getItem('intrlock_token') || localStorage.getItem('intrlock_jwt') || '';
        const credentials = {
          console_host: unifiConsoleHost.trim(),
          api_token: unifiApiToken.trim(),
          tls_fingerprint_sha256: unifiTlsFingerprint.replace(/:/g, '').trim().toUpperCase(),
        };
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` };

        // Verify before persisting. A failed reachability/auth/TLS check leaves
        // no credential row behind and returns only the adapter's redacted error.
        const verifyRes = await fetch(`${connectorBase}/integrations/unifi_access/verify`, {
          method: 'POST', headers, body: JSON.stringify({ credentials }),
        });
        const verifyData = await verifyRes.json().catch(() => ({}));
        if (!verifyRes.ok || verifyData?.valid !== true) {
          throw new Error(verifyData?.error || verifyData?.message || `UniFi verification failed (${verifyRes.status})`);
        }

        const enableRes = await fetch(`${connectorBase}/integrations/unifi_access/enable`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            entity_type: 'property',
            entity_id: selectedPropertyId,
            credentials,
            settings: {
              name: systemName.trim() || 'UniFi Access',
              api_surface: 'console_local',
              port: 12445,
              spec_date: '2026-03-23',
            },
            permission_mode: 'read_write',
            sync_direction: 'pull',
            source_of_truth: 'external',
          }),
        });
        const enableData = await enableRes.json().catch(() => ({}));
        if (!enableRes.ok) {
          throw new Error(enableData?.error || enableData?.message || `Could not enable UniFi Access (${enableRes.status})`);
        }

        // Inventory import is server-side: access-api invokes connector-api,
        // which decrypts the token only inside its Lambda and returns vendor
        // metadata. The browser never receives the saved credential again.
        const accessBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
        const syncRes = await fetch(`${accessBase}/acs-systems/connect-unifi-access`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            property_id: selectedPropertyId,
            name: systemName.trim() || 'UniFi Access',
            connection_id: enableData?.id || enableData?.integration_id || selectedPropertyId,
          }),
        });
        const syncData = await syncRes.json().catch(() => ({}));
        if (!syncRes.ok || syncData?.error) {
          throw new Error(syncData?.error || syncData?.message || `UniFi inventory sync failed (${syncRes.status})`);
        }

        await logAuditEvent({
          property_id: selectedPropertyId,
          acs_system_id: syncData?.system?.id,
          event_type: 'system_connected',
          event_source: 'unifi_access',
          description: `Connected UniFi Access system "${systemName || 'UniFi Access'}" — ${syncData?.counts?.total ?? 0} resources`,
          metadata: { provider: 'unifi_access', ...syncData?.counts },
        });
      } else if (provider === 'bridge') {
        await claimBridgeDevice(systemId.trim(), '', selectedPropertyId, systemName || 'Intrlock Bridge');
        await logAuditEvent({
          property_id: selectedPropertyId,
          event_type: 'system_connected',
          event_source: 'bridge',
          description: `Connected Intrlock Bridge "${systemName || 'Intrlock Bridge'}" (serial: ${systemId})`,
          metadata: { provider: 'bridge', serial_number: systemId },
        });
      }

      refreshAllCounters();
      closeModal();
      fetchACS();
    } catch (e: any) {
      setConnectError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (acs: ACSRecord) => {
    setTesting(acs.id);
    try {
      if (acs.provider === 'pdk' && acs.system_id) {
        await getPDKCloudNodes(acs.system_id);
      }
    } catch {}
    setTimeout(() => setTesting(null), 1000);
  };

  const toggleNode = (id: string) => {
    const s = new Set(selectedNodes);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedNodes(s);
  };

  const toggleDevice = (id: string) => {
    const s = new Set(selectedDevices);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedDevices(s);
  };

  // Group properties by customer
  const groupedProperties = customers.map((c: any) => ({
    customer: c,
    props: properties.filter((p: any) => String(p.customer_id) === String(c.uuid || c.id)),
  })).filter(g => g.props.length > 0);
  const ungrouped = properties.filter((p: any) => !customers.find((c: any) => String(c.uuid || c.id) === String(p.customer_id)));

  const selectedProp = properties.find((p: any) => String(p.uuid || p.id) === String(selectedPropertyId));

  const getDeviceKey = (dev: any) => String(dev.id);

  // Open the assign-property mini-modal for a system row.
  // A connected system is an authorized upstream resource. Assignment is to
  // any property in the signed-in user's RBAC scope, never its legacy customer anchor.
  const openAssignForRow = async (acs: ACSRecord) => {
    setAssignTarget(acs);
    setAssignPropertyId('');
    setAssignError('');
    setAssignLoading(true);
    try {
      setAssignProps(allProperties);
    } finally {
      setAssignLoading(false);
    }
  };

  // Submit the assign — tries PUT /acs-systems/{id}/assign-property first,
  // falls back to PATCH /acs-systems/{id} { property_id }.
  const submitAssign = async () => {
    if (!assignTarget || !assignPropertyId) return;
    setAssignLoading(true);
    setAssignError('');
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const tk = localStorage.getItem('intrlock_token') || '';
    const sysId = String(assignTarget.id);
    const propId = assignPropertyId;
    try {
      let ok = false;
      try {
        const r1 = await fetch(`${apiBase}/acs-systems/${encodeURIComponent(sysId)}/assign-property`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ property_id: propId }),
        });
        ok = r1.ok;
      } catch { /* try fallback */ }
      if (!ok) {
        const r2 = await fetch(`${apiBase}/acs-systems/${encodeURIComponent(sysId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
          body: JSON.stringify({ property_id: propId }),
        });
        ok = r2.ok;
        if (!ok) {
          const t = await r2.text().catch(() => '');
          throw new Error(t || `Assign failed (${r2.status})`);
        }
      }
      // Optimistic UI: reflect new property name immediately
      const chosen = assignProps.find((p: any) => String(p.uuid || p.id) === String(propId));
      const chosenName = chosen?.name || null;
      setAcsList((prev) => prev.map((r: any) => (
        String(r.id) === sysId
          ? { ...r, assigned_property_id: propId, assigned_property_name: chosenName }
          : r
      )));
      setAssignTarget(null);
      // Background refetch for authoritative counts
      fetchACS();
    } catch (e: any) {
      setAssignError(e?.message || 'Failed to assign property');
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <div className="shadow-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Control Systems</h1>
          <p className="text-gray-500 mt-1">Manage ACS integrations</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={fetchACS}
            disabled={loading}
            title="Reload from upstream ACS systems"
          >
            <svg className="w-4 h-4 mr-1.5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
          {canAddDevices((user?.role || 'viewer') as Role) && (
            <Button onClick={() => setShowAuthorizedSetup(true)}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add System
            </Button>
          )}
        </div>
      </div>
      <CountBand value={acsList.length} detail="Access-control systems available to this account" />
      <div className="prototype-toolbar access-control-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search systems, providers, IDs, or properties…" />
        <label><small>Provider</small><select value={providerFilter} onChange={e=>setProviderFilter(e.target.value)}><option value="all">All providers</option>{Array.from(new Set(acsList.map(a=>String(a.provider||'').toLowerCase()).filter(Boolean))).sort().map(p=><option key={p} value={p}>{p.replace(/_/g,' ')}</option>)}</select></label>
        <label><small>Status</small><select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="all">All statuses</option>{Array.from(new Set(acsList.map(a=>String(a.status||'unknown').toLowerCase()))).sort().map(s=><option key={s} value={s}>{s}</option>)}</select></label>
        <div className="prototype-view-toggle"><button className={viewMode==='table'?'active':''} onClick={()=>{setViewMode('table');localStorage.setItem('view:access-control','table')}}>Table</button><button className={viewMode==='grid'?'active':''} onClick={()=>{setViewMode('grid');localStorage.setItem('view:access-control','grid')}}>Grid</button></div>
      </div>

      {/* Mobile cards */}
      <div className={`${viewMode === 'grid' ? 'grid' : 'md:hidden grid'} mb-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2`}>
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">Loading…</div>
        ) : filteredAcs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No access control systems configured yet.</div>
        ) : filteredAcs.map((a: any) => {
          const props = getAssociatedProperties(a);
          const status = (a.status || 'unknown').toLowerCase();
          const sc = status === 'connected' || status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600';
          return (
            <div key={a.id} onClick={() => openDetail(a)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{a.name || a.provider || `ACS #${a.id}`}</h3>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc}`}>{status}</span>
              </div>
              <div className="customer-card-address"><span>Property</span><p>{props[0]?.name || a.assigned_property_name || 'Unassigned'}{props.length > 1 ? ` +${props.length - 1} more` : ''}</p></div>
              <div className="customer-card-contact"><span>Provider</span><strong className="capitalize">{a.provider || 'System'}</strong><span>System ID</span><strong>{a.system_id || a.pdk_system_id || 'Not reported'}</strong></div>
              <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}><summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary><div><article><span>Properties</span><strong>{props.length || (a.assigned_property_name ? 1 : 0)}</strong></article><article><span>Devices</span><strong>{getDeviceCount(a)}</strong></article><article><span>Cloud nodes</span><strong>{a.cloud_node_count || 0}</strong></article><article><span>Channels</span><strong>{a.channel_count || 0}</strong></article></div></details>
              <div className="mobile-card-actions">
                <button onClick={(e) => { e.stopPropagation(); openDetail(a); }} className="row-open">Manage <span aria-hidden="true">→</span></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <Card padding={false} className={viewMode === 'table' ? 'hidden md:block' : 'hidden'}>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filteredAcs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No access control systems configured yet.</div>
        ) : (
          (() => {
            // Flatten each system into one row per cloud node / site. A single
            // ACS system can host many cloud nodes, each potentially assigned
            // to a different Intrlock property — so the listing should reflect
            // that 1:N relationship instead of collapsing it.
            // One row per ACS system (= one row per (customer × upstream
            // system_id)). The system detail page is where the PM expands
            // into per-cloud-node + per-device assignments.
            const rows = filteredAcs.map((a: any) => ({ ...a, _system_id: a.id }));
            if (rows.length === 0) {
              return <div className="p-8 text-center text-gray-400">No access-control systems connected yet.</div>;
            }
            return (
              <Table
                columns={[
                  { key: 'name', header: 'Name', render: (r: any) => {
                    return r.name
                      ? <span className="text-gray-900 font-medium">{r.name}</span>
                      : <span className="text-gray-400">—</span>;
                  }},
                  { key: 'provider', header: 'System Provider', render: (r: any) => {
                    const provider = (r.provider || '').toLowerCase();
                    const label = provider === 'pdk' ? 'ProDataKey'
                      : provider === 'brivo' ? 'Brivo'
                      : provider === 'bridge' ? 'Intrlock Bridge'
                      : (LOCK_BRANDS.find(b => b.key === r.provider)?.name || provider.toUpperCase());
                    // §28.518.32 — shared ProviderPill (Google favicon + branded pill).
                    return <ProviderPill provider={provider} label={label} />;
                  }},
                  { key: 'assigned_property', header: 'Assigned Sites', render: (r: any) => (
                    <span className="text-gray-900 text-sm">{Number(r.cloud_node_count || 0)}</span>
                  )},
                  { key: 'status', header: 'Status', render: (r: any) => {
                    const s = r.status;
                    return <Badge color={s === 'connected' || s === 'active' ? 'green' : s === 'disconnected' ? 'red' : 'gray'}>{s || '—'}</Badge>;
                  }},
                  { key: 'intrlock_devices', header: 'Active Devices', render: (r: any) => {
                    return <span className="text-gray-700">{Number(r.assigned_device_count || 0)}</span>;
                  }},
                  { key: 'available_devices', header: 'Available Devices', render: (r: any) => {
                    return <span className="text-gray-700">{Number(r.device_count ?? r.opaque_device_count ?? 0)}</span>;
                  }},
                  { key: 'created_at', header: 'Created Date', render: (r: any) => {
                    return r.created_at
                      ? <span className="text-gray-700 text-sm">{formatDate(r.created_at)}</span>
                      : <span className="text-gray-400">—</span>;
                  }},
                  { key: 'actions', header: '', render: (r: any) => {
                    const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                    const handleDelete = async (e: React.MouseEvent) => {
                      e.stopPropagation();
                      const cust = allCustomers.find((c: any) => c.id === r.customer_id || c.uuid === r.customer_id);
                      const label = (cust?.name || cust?.company_name) || r.name || r.system_id || r.id || 'this system';
                      if (!window.confirm(`Delete ACS system "${label}"? Removes Intrlock's record (cloud-nodes, acs_devices, acs_holders) and any stale legacy entries. The upstream provider system itself is NOT touched.`)) return;
                      const tk = localStorage.getItem('intrlock_token') || '';
                      const headers = { Authorization: `Bearer ${tk}` };
                      // Try every identifier we know about — UUID first, then
                      // upstream system_id / pdk_system_id, then any string id.
                      // Stale rows from legacy sources may not have a UUID.
                      const candidates = [r.id, r.uuid, r.system_id, r.pdk_system_id]
                        .filter((x) => x != null && String(x).trim() !== '')
                        .map(String);
                      let success = false;
                      let lastErr = '';
                      for (const cand of candidates) {
                        try {
                          const resp = await fetch(`${apiBase}/acs-systems/${encodeURIComponent(cand)}`, { method: 'DELETE', headers });
                          if (resp.ok) { success = true; break; }
                          lastErr = await resp.text();
                        } catch (err: any) { lastErr = err?.message ?? String(err); }
                      }
                      if (!success) {
                        // Fall back to the legacy-cleanup endpoint (DDB-only
                        // orphan rows that never had a PG row).
                        try {
                          const resp = await fetch(`${apiBase}/acs-systems/cleanup-legacy`, {
                            method: 'POST',
                            headers: { ...headers, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ id: r.id, system_id: r.system_id, pdk_system_id: r.pdk_system_id, customer_id: r.customer_id, name: r.name }),
                          });
                          if (resp.ok) success = true; else lastErr = await resp.text();
                        } catch (err: any) { lastErr = err?.message ?? String(err); }
                      }
                      if (!success) { alert(`Delete failed: ${lastErr || 'unknown error'}`); return; }
                      await fetchACS();
                    };
                    return (
                      <button
                        onClick={handleDelete}
                        className="text-xs font-medium text-red-700 hover:text-red-800 px-2 py-1 rounded border border-red-200 hover:bg-red-50"
                        title="Delete ACS system"
                      >
                        Delete
                      </button>
                    );
                  }},
                ]}
                data={rows}
                onRowClick={(r: any) => navigate(`/access-control/${r._system_id}`)}
              />
            );
          })()
        )}
      </Card>

      <Modal open={showModal} onClose={closeModal} title={
        step === 1 ? 'Add ACS — Choose Customer'          :
        step === 2 ? 'Add ACS — Choose System Provider'   :
        step === 3 ? 'Add ACS — Secure authentication'    :
        step === 4 ? 'Add ACS — Select Sites' :
        step === 5 ? 'Add ACS — Select Devices' :
        step === 6 ? 'Add ACS — Assign to Property' :
        step === 7 ? 'Add ACS — Intrlock user groups' :
        'Add ACS — Assign ACS System to a Property (optional)'
      }>
        <div className="space-y-4">
          {connectError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{connectError}</div>
          )}

          {/* Step 1 — Customer selection (dealer / admin only; picks the customer this ACS will belong to) */}
          {step === 1 && (() => {
            // Scope the dropdown to customers the logged-in user can see:
            //   super_admin / admin → every customer
            //   dealer / integrator → customers under the user's dealer
            //   customer / property_manager → just their own customer
            const role = (user?.role || '').toLowerCase();
            return (
              <>
                <div className="border border-gray-200 bg-gray-50 p-4">
                  <p className="font-semibold text-gray-900">Use an authorized integration</p>
                  <p className="text-sm text-gray-600 mt-1">Systems available through your Integrations permissions can be added here. After choosing a system, assign any site or device to any property available in your Intrlock scope.</p>
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="secondary" onClick={closeModal}>Cancel</Button>
                  <Button onClick={() => setStep(2)}>Choose integration</Button>
                </div>
              </>
            );
          })()}

          {/* Step 2 — System provider (logo grid modal) */}
          {step === 2 && (
            <>
              <p className="text-sm text-gray-600">Pick an authorized ACS integration. Property assignment happens after system discovery.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                {[
                  { id: 'pdk',    name: 'ProDataKey (PDK)',   desc: 'Cloud panels & readers' },
                  { id: 'brivo',  name: 'Brivo',              desc: 'Enterprise cloud ACS' },
                  { id: 'bridge', name: 'Intrlock Bridge',    desc: 'Wired relay hardware' },
                  { id: 'cbw',    name: 'Control By Web',     desc: 'Modular relay & IP I/O' },
                  { id: 'unifi_access', name: 'UniFi Access', desc: 'Doors, readers & hubs' },
                ].map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setProvider(m.id as any)}
                    className={`p-4 border-2 rounded-lg text-center transition-all hover:shadow ${provider === m.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200 bg-white'}`}
                  >
                    <img src={`/mfg/${m.id === 'unifi_access' ? 'unifi' : m.id}.${m.id === 'bridge' ? 'svg' : 'png'}`} alt={m.name} className="w-12 h-12 mx-auto mb-2 object-contain" onError={(e:any)=>{e.target.style.display='none'}} />
                    <div className="text-sm font-semibold">{m.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
              <div className="flex gap-3 justify-end mt-2">
                <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={() => setStep(3)} disabled={!provider}>Next</Button>
              </div>
            </>
          )}

          {/* Step 3 — System ID + provider-specific creds */}
          {step === 3 && provider === 'cbw' && (
            <CBWSetupFlow
              entryPoint="access-control"
              customerId={selectedCustomerId}
              properties={properties}
              onSuccess={() => { closeModal(); fetchACS(); refreshAllCounters(); }}
              onCancel={closeModal}
              onError={(err) => setConnectError(err)}
            />
          )}
          {step === 3 && provider !== 'cbw' && (
            <>
              <Input label="System Nickname" placeholder="e.g., Main Building Locks" value={systemName} onChange={(e: any) => setSystemName(e.target.value)} />
              {provider === 'pdk' && (
                <Input
                  label="PDK System ID"
                  placeholder="e.g. de66983d-4dc4-4d20-b669-9b5135ace219"
                  value={systemId}
                  onChange={(e: any) => setSystemId(e.target.value)}
                  className="font-mono"
                />
              )}
              {provider === 'brivo' && (
                <div className="space-y-3">
                  <Input label="Brivo Admin Username" value={brivoUsername} onChange={(e: any) => setBrivoUsername(e.target.value)} placeholder="admin@company.com" />
                  <Input label="Brivo Admin Password" value={brivoPassword} onChange={(e: any) => setBrivoPassword(e.target.value)} placeholder="Password" type="password" />
                </div>
              )}
              {provider === 'bridge' && (
                <div>
                  <Input label="Device Serial Number" value={systemId} onChange={(e: any) => setSystemId(e.target.value)}
                    placeholder="Enter serial from Pi terminal or scan QR code" required />
                  <p className="text-xs text-gray-400 mt-1">Found in the Pi terminal after running the install script</p>
                </div>
              )}
              {provider === 'unifi_access' && (
                <div className="space-y-3">
                  <Input
                    label="UniFi Access Console"
                    value={unifiConsoleHost}
                    onChange={(e: any) => setUnifiConsoleHost(e.target.value)}
                    placeholder="Hostname or IP (Intrlock uses HTTPS port 12445)"
                    className="font-mono"
                  />
                  <Input
                    label="Access API Token"
                    value={unifiApiToken}
                    onChange={(e: any) => setUnifiApiToken(e.target.value)}
                    placeholder="Access → Settings → General → Advanced → API Token"
                    type="password"
                    autoComplete="new-password"
                  />
                  <Input
                    label="Console Certificate SHA-256 Fingerprint"
                    value={unifiTlsFingerprint}
                    onChange={(e: any) => setUnifiTlsFingerprint(e.target.value)}
                    placeholder="64 hex characters (colons are optional)"
                    className="font-mono"
                  />
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
                    Intrlock verifies the pinned console certificate and Bearer token before saving. The token is then encrypted by AWS KMS and is never returned to the browser.
                  </div>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                <Button variant="secondary" onClick={() => setStep(2)}>Back</Button>
                {provider === 'pdk' ? (
                  <Button onClick={handlePDKConnect} loading={connectLoading} disabled={!systemId.trim()}>Connect</Button>
                ) : provider === 'brivo' ? (
                  <Button onClick={handleBrivoConnect} loading={connectLoading} disabled={!brivoUsername || !brivoPassword}>Connect to Brivo</Button>
                ) : provider === 'unifi_access' ? (
                  <Button onClick={handleUnifiNext} loading={connectLoading} disabled={!unifiConsoleHost.trim() || !unifiApiToken.trim() || !unifiTlsFingerprint.trim()}>Choose Property</Button>
                ) : (
                  <Button onClick={handleBridgeNext} disabled={!systemId.trim()}>Next</Button>
                )}
              </div>
            </>
          )}

          {step === 4 && provider === 'pdk' && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">System: <strong>{systemName || systemId}</strong></p>
                <button
                  className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                  onClick={() => {
                    if (selectedNodes.size === cloudNodes.length) setSelectedNodes(new Set());
                    else setSelectedNodes(new Set(cloudNodes.map((n: any) => String(n.id))));
                  }}
                >
                  {selectedNodes.size === cloudNodes.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {cloudNodes.map((node: any) => (
                  <label key={node.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedNodes.has(String(node.id))}
                      onChange={() => toggleNode(String(node.id))}
                      className="rounded text-brand-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{node.name || `Site ${node.id}`}</p>
                      <p className="text-xs text-gray-500">{node.serialNumber || node.serial_number || ''}</p>
                    </div>
                    <Badge color={node.connected !== false ? 'green' : 'red'}>
                      {node.connected !== false ? 'Online' : 'Offline'}
                    </Badge>
                  </label>
                ))}
              </div>
              <div className="flex gap-3 justify-end">
                <Button variant="secondary" onClick={() => setStep(3)}>Back</Button>
                <Button onClick={handleLoadDevices}>
                  {selectedNodes.size === 0 ? 'Skip — assign later' : 'Next — Choose Property'}
                </Button>
              </div>
            </>
          )}

          {step === 5 && (() => {
            // Group devices by the property they'll land on. Each device's
            // parent cloud-node / site has been assigned to a property in
            // step 6's nodePropertyMap (or to the single selectedPropertyId
            // for the bulk case). Group by the resolved property name so
            // the PM can see which devices belong to which building.
            const groups = new Map<string, any[]>();
            for (const dev of devices) {
              const cnKey = String(dev._cloudNodeId || dev.cloud_node_id || dev.siteId || '');
              const propertyId = nodePropertyMap[cnKey] || selectedPropertyId || '';
              const propertyName = propertyId
                ? (properties.find((p: any) => String(p.uuid || p.id) === propertyId)?.name || 'Unassigned property')
                : (dev._cloudNodeName || dev.siteName || 'Unassigned');
              const arr = groups.get(propertyName) ?? groups.set(propertyName, []).get(propertyName)!;
              arr.push(dev);
            }
            const groupKeys = Array.from(groups.keys()).sort();
            return (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">{devices.length} devices found · grouped by property</p>
                  <button
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                    onClick={() => {
                      if (selectedDevices.size === devices.length) setSelectedDevices(new Set());
                      else setSelectedDevices(new Set(devices.map((d: any) => getDeviceKey(d))));
                    }}
                  >
                    {selectedDevices.size === devices.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-4">
                  {groupKeys.map((g) => {
                    const list = groups.get(g) || [];
                    return (
                      <div key={g}>
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-700">{g}</h4>
                          <button
                            className="text-xs text-brand-600 hover:underline"
                            onClick={() => {
                              const keys = list.map((d: any) => getDeviceKey(d));
                              const all = keys.every((k: string) => selectedDevices.has(k));
                              const next = new Set(selectedDevices);
                              if (all) keys.forEach((k: string) => next.delete(k));
                              else keys.forEach((k: string) => next.add(k));
                              setSelectedDevices(next);
                            }}
                          >Toggle group</button>
                        </div>
                        <div className="space-y-2">
                          {list.map((dev: any) => {
                            const key = getDeviceKey(dev);
                            return (
                              <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedDevices.has(key)}
                                  onChange={() => toggleDevice(key)}
                                  className="rounded text-brand-600"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">
                                    {dev.name || dev.description || `Device ${dev.id}`}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {dev.type || 'door'} · {dev._cloudNodeName || dev.siteName || ''}
                                  </p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3 justify-end">
                  <Button variant="secondary" onClick={() => setStep(4)}>Back</Button>
                  <Button onClick={handleChooseProperty} disabled={selectedDevices.size === 0}>
                    Next — Choose Property
                  </Button>
                </div>
              </>
            );
          })()}

          {step === 6 && (() => {
            // Properties scoped to the picked Intrlock customer — the PDK
            // system lives under that customer, so cloud nodes can only
            // be assigned to properties that customer owns.
            const customerProps = properties;
            const propertyDropdown = (value: string, onChange: (v: string) => void, includeBlank: string) => (
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                <option value="">{includeBlank}</option>
                {customerProps.map((p: any) => (
                  <option key={p.id} value={p.uuid || p.id}>{p.name}</option>
                ))}
              </select>
            );

            // PDK with multiple cloud nodes ticked → per-cloud-node mapping.
            const pdkMulti = provider === 'pdk' && selectedNodes.size > 0;
            const tickedNodes = cloudNodes.filter((n: any) => selectedNodes.has(String(n.id)));

            // Bulk-assign helper: pick one property and apply to every
            // ticked node at once.
            const bulkAssign = (propertyId: string) => {
              setSelectedPropertyId(propertyId);
              const next: Record<string, string> = { ...nodePropertyMap };
              for (const n of tickedNodes) next[String(n.id)] = propertyId;
              setNodePropertyMap(next);
            };

            const allMapped = pdkMulti
              ? tickedNodes.every((n: any) => nodePropertyMap[String(n.id)])
              : !!selectedPropertyId;

            return (
              <>
                {pdkMulti ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assign all cloud nodes to one property</label>
                      {propertyDropdown(selectedPropertyId, bulkAssign, '— Pick to apply to every cloud node below —')}
                      <p className="text-xs text-gray-500 mt-1">Or assign each cloud node to its own property below.</p>
                    </div>

                    <div className="border-t border-gray-100 pt-3 space-y-3 max-h-72 overflow-y-auto">
                      {tickedNodes.map((n: any) => (
                        <div key={n.id} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{n.name || `Site ${n.id}`}</p>
                            <p className="text-xs text-gray-500">{n.serialNumber || n.serial_number || ''}</p>
                          </div>
                          <div className="w-56">
                            {propertyDropdown(
                              nodePropertyMap[String(n.id)] || '',
                              (v) => setNodePropertyMap({ ...nodePropertyMap, [String(n.id)]: v }),
                              '— Property —',
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Property</label>
                    {propertyDropdown(selectedPropertyId, setSelectedPropertyId, '— Select a property —')}
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <Button variant="secondary" onClick={() => setStep(provider === 'bridge' || provider === 'unifi_access' ? 3 : provider === 'brivo' ? 5 : 4)}>Back</Button>
                  <Button onClick={handleSave} loading={saving} disabled={!allMapped}>
                    {provider === 'bridge' ? 'Connect Bridge' : provider === 'unifi_access' ? 'Verify & Enable UniFi' : provider === 'pdk' ? 'Save & continue to groups →' : 'Connect System'}
                  </Button>
                </div>
              </>
            );
          })()}

          {/* Step 7 — Intrlock user groups (§28.518.5) */}
          {step === 7 && (() => {
            const propIds = Object.keys(groupNames);
            const finish = async () => {
              setSaving(true);
              try {
                const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                const tk = localStorage.getItem('intrlock_token') || '';
                for (const pid of propIds) {
                  if (!groupAuto[pid]) continue;
                  const nm = (groupNames[pid] || '').trim();
                  if (!nm || !createdSystemId) continue;
                  const ext = 'intrlock-' + pid.replace(/-/g, '').slice(0, 16);
                  await fetch(`${apiBase}/acs-systems/${createdSystemId}/groups`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                    body: JSON.stringify({ external_id: ext, name: nm, property_id: pid }),
                  }).catch(() => {});
                }
                // Advance to optional system-level property-assign step.
                // Reuse the mini-modal state by pre-loading properties for
                // the wizard's picked customer.
                const custProps = properties;
                setAssignProps(custProps);
                setAssignPropertyId('');
                setAssignError('');
                setSaving(false);
                setStep(8);
              } catch { setSaving(false); }
            };
            return (
              <>
                <p className="text-sm text-gray-600 mb-2">
                  Intrlock creates a managed group per property to hold Intrlock-provisioned users (tenants, staff). Name each group to your liking. If unchecked, no group is created.
                </p>
                {propIds.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded text-sm">
                    No property assignments — go back and assign at least one panel to a property first.
                  </div>
                )}
                {propIds.map((pid) => {
                  const p: any = properties.find((x: any) => String(x.id) === String(pid) || String(x.uuid) === String(pid)) || {};
                  return (
                    <div key={pid} className="p-3 bg-gray-50 rounded-lg mb-2">
                      <div className="text-sm font-semibold mb-1">{p.name || pid}</div>
                      <Input
                        label="Group name for Intrlock users"
                        value={groupNames[pid] || ''}
                        onChange={(e: any) => setGroupNames({ ...groupNames, [pid]: e.target.value })}
                      />
                      <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!groupAuto[pid]}
                          onChange={(e) => setGroupAuto({ ...groupAuto, [pid]: e.target.checked })}
                        />
                        Auto-create this group when Intrlock users are assigned (recommended)
                      </label>
                    </div>
                  );
                })}
                <div className="flex gap-3 justify-end mt-3">
                  <Button variant="secondary" onClick={() => setStep(6)}>Back</Button>
                  <Button onClick={finish} loading={saving}>Next →</Button>
                </div>
              </>
            );
          })()}

          {/* Step 8 — Optional: assign the ACS *system itself* to an Intrlock Property.
              Complements the per-cloud-node assignment in Step 6. Users can skip. */}
          {step === 8 && (() => {
            const doAssign = async () => {
              if (!assignPropertyId || !createdSystemId) return;
              setSaving(true);
              setAssignError('');
              const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
              const tk = localStorage.getItem('intrlock_token') || '';
              try {
                let ok = false;
                try {
                  const r1 = await fetch(`${apiBase}/acs-systems/${encodeURIComponent(createdSystemId)}/assign-property`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                    body: JSON.stringify({ property_id: assignPropertyId }),
                  });
                  ok = r1.ok;
                } catch { /* fallback */ }
                if (!ok) {
                  const r2 = await fetch(`${apiBase}/acs-systems/${encodeURIComponent(createdSystemId)}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                    body: JSON.stringify({ property_id: assignPropertyId }),
                  });
                  ok = r2.ok;
                  if (!ok) throw new Error((await r2.text().catch(() => '')) || `Assign failed (${r2.status})`);
                }
                refreshAllCounters();
                closeModal();
                fetchACS();
              } catch (e: any) {
                setAssignError(e?.message || 'Failed to assign property');
              } finally {
                setSaving(false);
              }
            };
            const skip = () => {
              refreshAllCounters();
              closeModal();
              fetchACS();
            };
            return (
              <>
                <p className="text-sm text-gray-600">
                  Optionally link this ACS system to an Intrlock Property. You can do this later from the Access Control list.
                </p>
                {assignError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{assignError}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
                  <select
                    value={assignPropertyId}
                    onChange={(e) => setAssignPropertyId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="">— Select a property —</option>
                    {assignProps.map((p: any) => (
                      <option key={p.id || p.uuid} value={p.uuid || p.id}>{p.name}</option>
                    ))}
                  </select>
                  {assignProps.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">No properties for this customer.</p>
                  )}
                </div>
                <div className="flex gap-3 justify-end mt-3">
                  <Button variant="secondary" onClick={skip}>Skip for now</Button>
                  <Button onClick={doAssign} loading={saving} disabled={!assignPropertyId}>
                    Assign & Finish
                  </Button>
                </div>
              </>
            );
          })()}
        </div>
      </Modal>
      <AuthorizedResourceSetupModal open={showAuthorizedSetup} mode="system" customers={allCustomers.length ? allCustomers : customers} properties={allProperties.length ? allProperties : properties} onClose={() => setShowAuthorizedSetup(false)} />

      {/* Assign-to-Property mini-modal — triggered from the list row's "(Unassigned) — Assign" pill */}
      <Modal
        open={!!assignTarget}
        onClose={() => { setAssignTarget(null); setAssignError(''); }}
        title="Assign ACS system to a Property"
      >
        <div className="space-y-4">
          {assignError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{assignError}</div>
          )}
          <p className="text-sm text-gray-600">
            Link <b>{assignTarget?.name || 'this ACS system'}</b> to one of this customer's Intrlock properties.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
            <select
              value={assignPropertyId}
              onChange={(e) => setAssignPropertyId(e.target.value)}
              disabled={assignLoading}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">{assignLoading ? 'Loading…' : '— Select a property —'}</option>
              {assignProps.map((p: any) => (
                <option key={p.id || p.uuid} value={p.uuid || p.id}>{p.name}</option>
              ))}
            </select>
            {!assignLoading && assignProps.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">No properties found for this customer.</p>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => { setAssignTarget(null); setAssignError(''); }}>
              Skip for now
            </Button>
            <Button
              onClick={submitAssign}
              loading={assignLoading}
              disabled={!assignPropertyId || assignLoading}
            >
              Assign
            </Button>
          </div>
        </div>
      </Modal>

      <SystemDetailModal
        open={showDetail}
        onClose={() => { setShowDetail(false); setSelectedSystem(null); }}
        system={selectedSystem}
        properties={allProperties}
        onTestConnection={testConnection}
        testing={testing}
        onMutated={fetchACS}
      />
      <BulkActions selectedIds={selected} onClearSelection={clearSelection} onDelete={async (ids) => { await Promise.all(ids.map(id => apiDelete('access_control_systems', id))); refreshAllCounters(); fetchACS(); }} entityName="systems" />
    </div>
  );
};
